import useSWR, { KeyedMutator } from "swr"

import { Datasets } from "../../../../../constants/enums"
import { AppError } from "../../../../../errors/errorProcessors"
import { invalidApiDataError } from "../../../../../errors/recognisedErrors"
import { useGetUserData } from "../../../../../hooks/useGetUserData"
import { getClientProtected, getClientPublic } from "../../../clients/clients"
import { listAttachmentSummariesMapper } from "../../../mappers/registerDevice/listAttachmentSummariesMapper"
import {
    TAttachmentSummary,
    TListAttachmentSummariesResponse,
} from "../../../types/devices/ttr/listAttachmentSummariesTypes"
import { LIST_ATTACHMENT_SUMMARIES } from "../../../urls/private"

interface IUseUploadedDeviceAttachmentsOutput {
    uploadedAttachments?: TAttachmentSummary[]
    isLoading: boolean
    isValidating: boolean
    error?: AppError
    mutate: KeyedMutator<TListAttachmentSummariesResponse>
}

/** Hook for getting all uploaded attachments for a provided device reference */
const useUploadedDeviceAttachments = (
    dataset: Datasets,
    deviceRef?: string,
    attachmentUuid?: string,
    isLctAmendment?: boolean
): IUseUploadedDeviceAttachmentsOutput => {
    const { isSignedIn } = useGetUserData()

    const { data, error, isLoading, isValidating, mutate } = useSWR(
        LIST_ATTACHMENT_SUMMARIES(
            dataset,
            deviceRef,
            attachmentUuid,
            isSignedIn,
            isLctAmendment
        ),
        async (url: string): Promise<TListAttachmentSummariesResponse> => {
            const client = isSignedIn ? getClientProtected : getClientPublic
            try {
                const response = await client(url)
                return listAttachmentSummariesMapper.map(response)
            } catch {
                throw invalidApiDataError("Failed to get uploaded files")
            }
        }
    )

    return {
        uploadedAttachments: data?.attachment_summaries,
        isLoading,
        isValidating,
        error,
        mutate,
    }
}

export default useUploadedDeviceAttachments
